import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import {
  Box,
  Checkbox,
  IconButton,
  Link,
  TextField,
  useTheme
} from '@mui/material';
import { BudgetValueState, Income, IncomeMonth } from 'common/entities';
import { ITableColumn } from 'common/types';
import SortableTable from 'components/Shared/SortableTable';
import EditableNumberCell from 'components/table/EditableNumberCell';
import { useAppSelector } from 'core/hooks/hooks';
import { sumWithCurrency } from 'core/services/currency.service';
import { setIncome } from 'core/store/actions/incomes.actions';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { BudgetMonthContext } from './BudgetMonthContextProvider';
import BudgetMonthPanel from './BudgetMonthPanel';
import { setMonthIncome } from 'core/store/actions/budgetMonth.actions';

export interface BudgetMonthIncomesPanelProps {
  incomes: Income[];
  onCreateIncome: () => void;
}

interface IMonthIncomeRecord {
  id: string;
  name: string;
  income?: number;
  selected: boolean;
}

const getIncomesColumns = (isEditing: boolean) =>
  [
    ...(isEditing
      ? [
          {
            id: 'selected',
            numeric: false,
            label: ''
          }
        ]
      : []),
    {
      id: 'name',
      numeric: false,
      label: 'Name'
    },
    {
      id: 'income',
      numeric: true,
      label: 'Income'
    }
  ] as ITableColumn<IMonthIncomeRecord>[];

function createIncomeRow(
  income: Income,
  incomeValue: number | undefined,
  selected: boolean
): IMonthIncomeRecord {
  return {
    id: income.id,
    name: income.name,
    income: incomeValue,
    selected: selected
  };
}

const createRows = (
  incomes: Income[],
  selectedIncomes: Record<string, BudgetValueState>
) => {
  const incomeRows = incomes.map(b =>
    createIncomeRow(
      b,
      selectedIncomes[b.id]?.value,
      selectedIncomes[b.id]?.selected ?? false
    )
  );
  return incomeRows;
};

const renderBudgetSumItem = (value: any) => <>{sumWithCurrency(value)}</>;

const AddItemButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton onClick={onClick}>
    <AddCircleOutlinedIcon />
  </IconButton>
);

const BudgetMonthIncomesPanel = ({
  incomes,
  onCreateIncome
}: BudgetMonthIncomesPanelProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const incomeMonth = useAppSelector(periodSelector);

  const {
    isEditing,
    incomesValues,
    selectedIncomes,
    onUpdateIncomeValue,
    onIncomeToggleSelected
  } = useContext(BudgetMonthContext);

  const incomesColumns = useMemo(
    () => getIncomesColumns(isEditing),
    [isEditing]
  );

  const incomeRows = useMemo(
    () => createRows(incomes, selectedIncomes),
    [incomes, selectedIncomes]
  );

  const handleUpdateMonthIncomeSum = useCallback(
    (incomeId: string, income: number | undefined) => {
      dispatch(
        setMonthIncome({
          ...incomeMonth,
          incomeId,
          income
        })
      );
    },
    [dispatch, incomeMonth]
  );

  const incomesCustomCellsBuilder = useCallback(
    (
      column: keyof IMonthIncomeRecord,
      row: IMonthIncomeRecord
    ): JSX.Element => {
      switch (column) {
        case 'selected':
          return (
            <Checkbox
              checked={!!incomesValues[row.id]?.selected}
              onChange={() => onIncomeToggleSelected(row.id)}
            />
          );
        case 'income':
          return isEditing ? (
            <TextField
              sx={{ width: '80px' }}
              type="number"
              variant="standard"
              value={incomesValues[row.id]?.value ?? ''}
              onChange={e =>
                onUpdateIncomeValue(
                  row.id,
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              disabled={!incomesValues[row.id]?.selected}
            />
          ) : (
            <EditableNumberCell
              initialValue={Number(row.income) ?? 0}
              onSaveValue={(value: number) =>
                handleUpdateMonthIncomeSum(row.id, value)
              }
              renderValue={renderBudgetSumItem}
            />
          );
        default:
          return <>{row[column]}</>;
      }
    },
    [isEditing, incomesValues]
  );

  return (
    <BudgetMonthPanel
      title="Incomes"
      actions={<AddItemButton onClick={onCreateIncome} />}
    >
      {incomes.length ? (
        <SortableTable
          columns={incomesColumns}
          rows={incomeRows}
          indexBy="id"
          defaultOrderBy={'name'}
          customCellBuilder={incomesCustomCellsBuilder}
          styles={{
            cell: { borderBottom: 'none', px: 0, py: '.3rem' },
            headCell: { borderBottom: 'none', px: 0, py: '.3rem' }
          }}
        />
      ) : (
        <Box
          sx={{
            background: theme.palette.grey[50],
            p: 1,
            borderRadius: '10px'
          }}
        >
          No incomes.{' '}
          {
            <Link href="#" onClick={onCreateIncome}>
              Add a new one
            </Link>
          }
        </Box>
      )}
    </BudgetMonthPanel>
  );
};

export default BudgetMonthIncomesPanel;
